import React, { useEffect, useRef, useState } from 'react';
import Window from '../os/Window';

const Breakout: React.FC<WindowAppProps> = ({ onClose, onInteract, onMinimize }) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [running, setRunning] = useState(true);
    const [level, setLevel] = useState(1);
    const [score, setScore] = useState(0);
    const [lives, setLives] = useState(3);
    const [canvasSize, setCanvasSize] = useState({ w: 800, h: 600 });

    const gameStateRef = useRef({
        ballX: 400,
        ballY: 450,
        ballVX: 3,
        ballVY: -3,
        paddleX: 350,
        paddleWidth: 120,
        bricks: [] as { x: number; y: number; w: number; h: number; hit: boolean; row: number }[],
        cols: 10,
        rows: 5,
        brickW: 60,
        brickH: 20,
        gap: 8,
        canvasW: 800,
        canvasH: 600,
        leftPressed: false,
        rightPressed: false,
    });

    const initLevel = (lvl: number) => {
        const st = gameStateRef.current;
        st.cols = 10;
        st.rows = Math.min(6, 4 + lvl); // più righe ai livelli alti
        st.brickW = Math.floor((st.canvasW - st.gap * (st.cols + 1)) / st.cols);
        st.brickH = 20;
        st.bricks = [];
        for (let r = 0; r < st.rows; r++) {
            for (let c = 0; c < st.cols; c++) {
                const x = st.gap + c * (st.brickW + st.gap);
                const y = st.gap + r * (st.brickH + st.gap) + 40;
                st.bricks.push({ x, y, w: st.brickW, h: st.brickH, hit: false, row: r });
            }
        }
        // reset palla
        st.ballX = st.canvasW / 2;
        st.ballY = st.canvasH - 100;
        st.ballVX = 3 + lvl * 0.3;
        st.ballVY = -3 - lvl * 0.3;
        st.paddleX = (st.canvasW - st.paddleWidth) / 2;
    };

    // Resize canvas to fit container
    useEffect(() => {
        const updateSize = () => {
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                const w = Math.floor(rect.width - 24);
                const h = Math.floor(rect.height - 24);
                setCanvasSize({ w, h });
                gameStateRef.current.canvasW = w;
                gameStateRef.current.canvasH = h;
                // Reposition ball and paddle proportionally
                gameStateRef.current.paddleX = (w - gameStateRef.current.paddleWidth) / 2;
                gameStateRef.current.ballX = w / 2;
                gameStateRef.current.ballY = h - 100;
            }
        };
        updateSize();
        window.addEventListener('resize', updateSize);
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    useEffect(() => {
        initLevel(level);
        // input tastiera
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') gameStateRef.current.leftPressed = true;
            if (e.key === 'ArrowRight') gameStateRef.current.rightPressed = true;
            if (e.key.toLowerCase() === 'p') setRunning((r) => !r);
            if (e.key.toLowerCase() === 'r') {
                setScore(0);
                setLives(3);
                setLevel(1);
                initLevel(1);
                setRunning(true);
            }
        };
        const handleKeyUp = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') gameStateRef.current.leftPressed = false;
            if (e.key === 'ArrowRight') gameStateRef.current.rightPressed = false;
        };
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        let anim: number;

        const drawCRT = () => {
            ctx.save();
            ctx.globalAlpha = 0.08;
            for (let y = 0; y < gameStateRef.current.canvasH; y += 3) {
                ctx.fillStyle = '#0a0';
                ctx.fillRect(0, y, gameStateRef.current.canvasW, 1);
            }
            ctx.restore();
        };

        const step = () => {
            const st = gameStateRef.current;
            if (running) {
                // aggiornamento paddle
                if (st.leftPressed) st.paddleX -= 6;
                if (st.rightPressed) st.paddleX += 6;
                st.paddleX = Math.max(0, Math.min(st.canvasW - st.paddleWidth, st.paddleX));

                // aggiornamento palla
                st.ballX += st.ballVX;
                st.ballY += st.ballVY;

                // collisioni muro
                if (st.ballX < 0) { st.ballX = 0; st.ballVX *= -1; }
                if (st.ballX > st.canvasW) { st.ballX = st.canvasW; st.ballVX *= -1; }
                if (st.ballY < 0) { st.ballY = 0; st.ballVY *= -1; }

                // collisione paddle
                const paddleY = st.canvasH - 40;
                if (
                    st.ballY >= paddleY - 8 &&
                    st.ballY <= paddleY + 12 &&
                    st.ballX >= st.paddleX &&
                    st.ballX <= st.paddleX + st.paddleWidth
                ) {
                    st.ballY = paddleY - 8;
                    const hitPos = (st.ballX - st.paddleX) / st.paddleWidth; // 0..1
                    const angle = (hitPos - 0.5) * Math.PI / 3; // +/- 60°
                    const speed = Math.hypot(st.ballVX, st.ballVY);
                    st.ballVX = Math.cos(angle) * speed;
                    st.ballVY = -Math.abs(Math.sin(angle) * speed);
                }

                // collisione con mattoni
                for (let i = 0; i < st.bricks.length; i++) {
                    const b = st.bricks[i];
                    if (b.hit) continue;
                    if (
                        st.ballX >= b.x && st.ballX <= b.x + b.w &&
                        st.ballY >= b.y && st.ballY <= b.y + b.h
                    ) {
                        b.hit = true;
                        setScore((s) => s + 10);
                        // inverti direzione verticale
                        st.ballVY *= -1;
                        break;
                    }
                }

                // fuori dallo schermo in basso
                if (st.ballY > st.canvasH + 10) {
                    setLives((v) => v - 1);
                    // reset palla
                    st.ballX = st.canvasW / 2;
                    st.ballY = st.canvasH - 100;
                    st.ballVX = 3 + level * 0.3;
                    st.ballVY = -3 - level * 0.3;
                }

                // livello completato?
                if (st.bricks.every((b) => b.hit)) {
                    const next = level + 1;
                    setLevel(next);
                    initLevel(next);
                }
            }

            // draw
            ctx.clearRect(0, 0, st.canvasW, st.canvasH);
            // sfondo CRT
            ctx.fillStyle = '#000';
            ctx.fillRect(0, 0, st.canvasW, st.canvasH);

            // mattoni con colori originali (rosso, arancione, giallo)
            ctx.shadowColor = '#0f0';
            ctx.shadowBlur = 8;
            for (const b of st.bricks) {
                if (b.hit) continue;

                // Colori originali Breakout: rosso per le prime 2 righe, arancione per le 2 successive, giallo per le ultime
                let color = '#ffff00'; // giallo (default)
                if (b.row === 0 || b.row === 1) {
                    color = '#ff0000'; // rosso
                } else if (b.row === 2 || b.row === 3) {
                    color = '#ff8800'; // arancione
                }

                ctx.fillStyle = color;
                ctx.fillRect(b.x, b.y, b.w, b.h);
                ctx.strokeStyle = color;
                ctx.lineWidth = 2;
                ctx.strokeRect(b.x, b.y, b.w, b.h);
            }

            // paddle
            ctx.fillStyle = '#0f0';
            ctx.fillRect(st.paddleX, st.canvasH - 40, st.paddleWidth, 10);

            // palla
            ctx.beginPath();
            ctx.arc(st.ballX, st.ballY, 8, 0, Math.PI * 2);
            ctx.fillStyle = '#0f0';
            ctx.fill();

            // HUD
            ctx.shadowBlur = 0;
            ctx.fillStyle = '#0f0';
            ctx.font = '16px monospace';
            ctx.fillText(`Punteggio: ${score}   Vite: ${lives}   Livello: ${level}` , 16, 24);
            ctx.fillText(`Tasti: ← → muovi, P pausa, R reset`, 16, 44);
            if (!running) ctx.fillText('In pausa', st.canvasW - 100, 24);

            drawCRT();

            // game over
            if (lives <= 0) {
                ctx.fillStyle = '#0f0';
                ctx.font = '24px monospace';
                ctx.fillText('Game Over - premi R per ricominciare', 80, st.canvasH / 2);
            }

            anim = requestAnimationFrame(step);
        };

        anim = requestAnimationFrame(step);
        return () => cancelAnimationFrame(anim);
    }, [running, level, lives, score]);

    useEffect(() => {
        // quando finisci le vite, fermiamo il gioco finché non si fa reset
        if (lives <= 0) {
            setRunning(false);
        }
    }, [lives]);

    return (
        <Window
            top={20}
            left={200}
            width={920}
            height={750}
            windowBarIcon={"windowGameIcon"}
            windowTitle={"Breakout"}
            closeWindow={onClose}
            onInteract={onInteract}
            minimizeWindow={onMinimize}
            bottomLeftText={'© Copyright 2025 Davide Marangoni'}
        >
            <div ref={containerRef} style={styles.container}>
                <canvas
                    ref={canvasRef}
                    width={canvasSize.w}
                    height={canvasSize.h}
                    style={styles.canvas}
                />
            </div>
        </Window>
    );
};

const styles: StyleSheetCSS = {
    container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
        padding: 12,
        boxSizing: 'border-box',
        backgroundColor: '#000',
    },
    canvas: {
        imageRendering: 'pixelated',
        backgroundColor: '#000',
        width: '100%',
        height: '100%',
    },
};

export default Breakout;